import { cx } from '@linaria/core';
import { Button } from '@purinanbm/pds-ui';
import { GatsbyImage } from 'gatsby-plugin-image';
import { sendIt } from 'gatsby-plugin-purina-analytics/common/functions';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import * as React from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import IngredientModal from '../ingredients/IngredientModal';

interface IngredientProps {
  ingredients: TaxonomyTermIngredient[];
  headingLevel?: HeadingLevel;
  title?: string;
  bgColor?: 'white' | 'light';
}

const ProductIngredients: React.FC<IngredientProps> = function ({
  ingredients,
  title,
  headingLevel = 'h2',
  bgColor = 'light',
}) {
  const [showAll, setShowAll] = React.useState(false);
  const [modalId, setModalId] = React.useState('');
  const ref = React.useRef<React.ElementRef<'div'>>(null);

  React.useEffect(() => {
    if (showAll) {
      ref.current?.focus();
    }
  }, [showAll]);

  const { t } = useTranslation();

  const handleCloseModal = () => {
    setModalId('');
  };

  const handleShow = () => {
    setShowAll(!showAll);
  };

  const HeadingLevel = headingLevel;

  return (
    <div>
      <Container
        className={cx(
          bgColor ? `bg-${bgColor}` : 'bg-light',
          'pds-rounded-md !pds-p-4 md-bt:!pds-p-5',
        )}
      >
        {title && (
          <Row className="pds-text-center">
            <HeadingLevel className="pds-text-title-sm">{title}</HeadingLevel>
          </Row>
        )}
        <Row className="!pds-my-4 pds-overflow-auto pds-py-3 pds-gy-4">
          {ingredients.map((ingredient, index) => {
            const hide = index > 5 && !showAll ? 'pds-hidden' : 'pds-block';
            const image = ingredient.relationships?.field_image?.gatsbyImage;
            const handleGA = () => {
              sendIt({
                event: 'module_interaction',
                eventParams: {
                  component_id: ingredient.name,
                  component_type: 'ingredient_modal',
                  content_name: 'ingredient',
                },
              });
            };
            return (
              <React.Fragment key={ingredient.id}>
                <Col xs={6} sm={4} lg={3} xl={2} className={`${hide} pds-text-center`}>
                  <div>
                    <button
                      type="button"
                      className="pds-inline-flex pds-flex-col pds-items-center pds-text-center"
                      onClick={() => {
                        handleGA();
                        setModalId(ingredient.id);
                      }}
                      tabIndex={0}
                      ref={index === 6 ? ref : undefined}
                    >
                      <div className="pds-mb-4 pds-inline-flex pds-rounded-circle pds-border-none pds-bg-white pds-p-4.5">
                        {image ? (
                          <GatsbyImage
                            alt=""
                            image={image}
                            style={{ maxHeight: '100px', maxWidth: '100px' }}
                          />
                        ) : (
                          <div className="pds-block pds-h-full pds-max-h-[100px] pds-w-full pds-max-w-[100px] pds-rounded pds-bg-surface-layer">
                            <svg
                              aria-hidden
                              className="pds-h-full pds-w-full"
                              viewBox="0 0 100 100"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path d="" fill="none" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <span
                        style={{ wordBreak: 'break-word' }}
                        className="pds-hyphens-auto pds-font-bold"
                      >
                        {ingredient.name}
                      </span>
                    </button>
                  </div>
                </Col>
                <IngredientModal
                  ingredient={ingredient}
                  show={modalId === ingredient.id}
                  handleClose={handleCloseModal}
                />
              </React.Fragment>
            );
            return null;
          })}
        </Row>
        <div className="pds-mt-5 pds-flex pds-justify-center">
          <Button
            onClick={handleShow}
            buttonColor="neutral"
            buttonStyle={bgColor === 'white' ? 'solid' : 'outlined'}
            className="pds-px-5"
          >
            {showAll ? t('Show Fewer Ingredients') : t('View All Ingredients')}
          </Button>
        </div>
      </Container>
    </div>
  );
};

export default ProductIngredients;
